import React, { useCallback, useEffect, useState } from 'react';
import { Bell, BellOff, Smartphone, Monitor, Trash2, Send, Share, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  diagnosticoPush,
  suscripcionActual,
  activarAvisosAdmin,
  listarDispositivosAdmin,
  quitarDispositivoAdmin,
  probarAvisosEsteDispositivo,
  describirDispositivo,
} from '../../utils/pushSubscription';

const fecha = (iso) => (iso
  ? new Date(iso).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
  : null);

/**
 * Avisos push del comercio: activar este dispositivo, ver/quitar los demás y
 * mandar una prueba. Los avisos llegan aunque el navegador esté cerrado
 * (pedidos nuevos de la tienda y stock crítico).
 * Lo usa TiendaConfig → pestaña Notificaciones.
 */
export default function PushDispositivosPanel() {
  const [diag, setDiag] = useState(() => diagnosticoPush());
  const [dispositivos, setDispositivos] = useState([]);
  const [endpointLocal, setEndpointLocal] = useState(null);
  const [cargando, setCargando] = useState(false);

  const refrescar = useCallback(async () => {
    setDiag(diagnosticoPush());
    try {
      const [lista, sub] = await Promise.all([listarDispositivosAdmin(), suscripcionActual()]);
      setDispositivos(lista);
      setEndpointLocal(sub?.endpoint || null);
    } catch {
      setDispositivos([]);
    }
  }, []);

  useEffect(() => { refrescar(); }, [refrescar]);

  const esteActivo = Boolean(endpointLocal && dispositivos.some((d) => d.endpoint === endpointLocal));

  const activar = async () => {
    setCargando(true);
    try {
      await activarAvisosAdmin();
      toast.success('Avisos activados en este dispositivo.');
      await refrescar();
    } catch (e) {
      toast.error(e.message || 'No se pudieron activar los avisos.');
      setDiag(diagnosticoPush());
    } finally {
      setCargando(false);
    }
  };

  const probar = async () => {
    try {
      await probarAvisosEsteDispositivo();
      toast.success('Prueba enviada: debería llegar en unos segundos.');
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar la prueba.');
    }
  };

  const quitar = async (id) => {
    try {
      await quitarDispositivoAdmin(id);
      toast.success('Dispositivo quitado.');
      await refrescar();
    } catch {
      toast.error('No se pudo quitar el dispositivo.');
    }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 border border-slate-100 rounded-xl">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-xs font-bold text-slate-700 flex items-center gap-2">
              {esteActivo ? <Bell size={14} className="text-emerald-500" /> : <BellOff size={14} className="text-slate-400" />}
              Avisos push en este dispositivo
            </span>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Pedidos nuevos y stock crítico, aunque el navegador esté cerrado o el celular bloqueado.
            </p>
          </div>
          {diag.estado === 'disponible' && (
            esteActivo ? (
              <button type="button" onClick={probar}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-50">
                <Send size={12} /> Enviar prueba
              </button>
            ) : (
              <button type="button" onClick={activar} disabled={cargando}
                className="shrink-0 px-3 py-2 rounded-lg bg-slate-900 text-white text-[11px] font-bold hover:bg-slate-800 disabled:opacity-50">
                {cargando ? 'Activando…' : 'Activar'}
              </button>
            )
          )}
        </div>

        {diag.estado === 'requiere_instalar' && (
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-100 text-[11px] text-amber-800 leading-relaxed">
            <p className="font-bold flex items-center gap-1.5"><Share size={12} /> En iPhone/iPad hay que instalar la app</p>
            <p className="mt-1">
              Tocá <b>Compartir</b> → <b>Agregar a pantalla de inicio</b>, abrí Impulza desde ese ícono y volvé a esta pantalla para activar.
              Requiere iOS/iPadOS 16.4 o superior.
            </p>
          </div>
        )}
        {diag.estado === 'bloqueado' && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-100 text-[11px] text-red-700 leading-relaxed">
            <p className="font-bold flex items-center gap-1.5"><Lock size={12} /> Notificaciones bloqueadas</p>
            <p className="mt-1">
              Tocá el candado de la barra de direcciones → <b>Notificaciones</b> → <b>Permitir</b>, recargá la página y activá de nuevo.
            </p>
          </div>
        )}
        {diag.estado === 'no_soportado' && (
          <p className="mt-3 text-[11px] text-slate-500">Este navegador no admite notificaciones push. Probá con Chrome, Edge o Firefox.</p>
        )}
      </div>

      {dispositivos.length > 0 && (
        <div className="p-3 border border-slate-100 rounded-xl">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Tus dispositivos con avisos</p>
          <ul className="divide-y divide-slate-100">
            {dispositivos.map((d) => {
              const esEste = d.endpoint === endpointLocal;
              const movil = /iPhone|iPad|Android/.test(d.user_agent || '');
              return (
                <li key={d.id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex items-center gap-2">
                    {movil ? <Smartphone size={14} className="text-slate-400 shrink-0" /> : <Monitor size={14} className="text-slate-400 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-slate-700 truncate">
                        {describirDispositivo(d.user_agent)}
                        {esEste && <span className="ml-2 text-[9px] font-black uppercase text-emerald-600">este</span>}
                      </p>
                      <p className="text-[10px] text-slate-400">
                        Activado {fecha(d.created_at)}
                        {d.last_success_at ? ` · último aviso ${fecha(d.last_success_at)}` : ''}
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={() => quitar(d.id)} title="Quitar"
                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0">
                    <Trash2 size={14} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
